import { version as currentVersion } from "../../package.json";

export const APP_VERSION = currentVersion;

export const checkAppVersion = async () => {
    try {
        if (
            typeof navigator !== "undefined" &&
            navigator.serviceWorker &&
            typeof navigator.serviceWorker.getRegistrations === "function"
        ) {
            const registrations =
                await navigator.serviceWorker.getRegistrations();
            if (Array.isArray(registrations)) {
                registrations.forEach((r) => {
                    if (r && typeof r.update === "function") {
                        r.update().catch(() => {});
                    }
                });
            }
        }
    } catch (e) {
        // ignore service worker inspection errors
    }

    const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
    });
    if (res && res.ok === false) {
        throw new Error(`Version check failed (${res.status})`);
    }
    const data =
        res && typeof res.json === "function" ? await res.json() : res || {};
    const remoteVersion = (data && data.version) || currentVersion;

    let apiVersion = null;
    let timeoutId = null;
    try {
        const apiBase =
            process.env.REACT_APP_API || "https://api.rajchandra.me";
        const controller =
            typeof AbortController !== "undefined"
                ? new AbortController()
                : null;
        if (controller) {
            timeoutId = setTimeout(() => controller.abort(), 4000);
        }
        const apiResponse = await fetch(`${apiBase}/version`, {
            cache: "no-store",
            ...(controller ? { signal: controller.signal } : {}),
        });
        if (apiResponse && apiResponse.ok !== false) {
            const apiRes =
                typeof apiResponse.json === "function"
                    ? await apiResponse.json()
                    : apiResponse;
            apiVersion =
                (apiRes && apiRes.data && apiRes.data.version) ||
                (apiRes && apiRes.version) ||
                null;
        }
    } catch (e) {
        // gracefully ignore API version fetch errors in offline/test environments
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }

    const updateAvailable = Boolean(
        remoteVersion && remoteVersion !== currentVersion
    );

    return {
        currentVersion,
        remoteVersion,
        apiVersion,
        updateAvailable,
        checkedAt: Date.now(),
    };
};

export const applyAppUpdate = async () => {
    try {
        if (
            typeof window !== "undefined" &&
            "caches" in window &&
            window.caches &&
            typeof window.caches.keys === "function"
        ) {
            const keys = await window.caches.keys();
            await Promise.all(keys.map((k) => window.caches.delete(k)));
        }
    } catch (e) {
        // ignore cache purge errors
    }

    try {
        if (
            typeof navigator !== "undefined" &&
            "serviceWorker" in navigator &&
            navigator.serviceWorker &&
            typeof navigator.serviceWorker.getRegistrations === "function"
        ) {
            const regs = await navigator.serviceWorker.getRegistrations();
            if (Array.isArray(regs)) {
                regs.forEach((r) => {
                    const worker = r && (r.waiting || r.active || r.installing);
                    if (worker && typeof worker.postMessage === "function") {
                        worker.postMessage({ type: "SKIP_WAITING" });
                        worker.postMessage({ type: "CLEAR_CACHES" });
                    }
                });
                await Promise.all(
                    regs.map((r) =>
                        r && typeof r.unregister === "function"
                            ? r.unregister()
                            : Promise.resolve()
                    )
                );
            }
        }
    } catch (e) {
        // ignore service worker unregister errors
    }

    try {
        if (typeof localStorage !== "undefined") {
            localStorage.removeItem("binance_supported_currencies");
        }
    } catch (e) {
        // ignore localStorage errors
    }

    if (typeof window !== "undefined" && window.location) {
        try {
            const url = new URL(
                window.location.href ||
                    window.location.origin ||
                    "http://localhost"
            );
            url.searchParams.set("v", String(Date.now()));
            if (typeof window.location.replace === "function") {
                window.location.replace(url.toString());
                return;
            }
        } catch (e) {
            // fallback to reload
        }
        if (typeof window.location.reload === "function") {
            window.location.reload();
        }
    }
};

export const clearCacheAndReload = applyAppUpdate;

export default {
    APP_VERSION,
    checkAppVersion,
    applyAppUpdate,
    clearCacheAndReload,
};
